/**
 * WebGPU inference coordinator — uses a Web Worker so the main thread
 * (and the UI) stays fully responsive during model loading and generation.
 */

export const WEBGPU_MODELS = [
  { id: 'onnx-community/gemma-4-E2B-it-ONNX',    name: 'Gemma 4 E2B',    size: '1.4 GB',  dtype: 'q4f16', recommended: true },
  { id: 'onnx-community/Qwen2.5-1.5B-Instruct',   name: 'Qwen 2.5 1.5B', size: '~950 MB', dtype: 'q4' },
  { id: 'onnx-community/Llama-3.2-1B-Instruct',   name: 'Llama 3.2 1B',  size: '~700 MB', dtype: 'q4' },
  { id: 'onnx-community/gemma-3-1b-it-ONNX',      name: 'Gemma 3 1B',    size: '~550 MB', dtype: 'q4', fast: true },
];

let _worker = null;
let _loaded = false;
let _loadedModelId = null;

function getWorker() {
  if (!_worker) {
    _worker = new Worker(new URL('./webgpu.worker.js', import.meta.url), { type: 'module' });
  }
  return _worker;
}

export function resetWebGPUWorker() {
  if (_worker) {
    _worker.terminate();
    _worker = null;
  }
  _loaded = false;
  _loadedModelId = null;
}

export function isWebGPUSupported() {
  return typeof navigator !== 'undefined' && 'gpu' in navigator;
}

export function isModelLoaded() {
  return _loaded;
}

export function getLoadedModelId() {
  return _loadedModelId;
}

export function loadWebGPUModel(modelId, onProgress) {
  const mid = modelId || 'onnx-community/gemma-4-E2B-it-ONNX';
  const modelInfo = WEBGPU_MODELS.find((m) => m.id === mid) || WEBGPU_MODELS[0];

  // Already loaded the right model
  if (_loaded && _loadedModelId === mid) return Promise.resolve();

  // Different model requested — reset and reload
  if (_loaded && _loadedModelId !== mid) resetWebGPUWorker();

  if (!isWebGPUSupported()) {
    return Promise.reject(new Error('WebGPU is not supported. Use Chrome 113+ or Edge 113+.'));
  }

  return new Promise((resolve, reject) => {
    const worker = getWorker();

    const handler = ({ data }) => {
      if (data.type === 'progress') {
        onProgress?.(data);
      } else if (data.type === 'loaded') {
        _loaded = true;
        _loadedModelId = mid;
        worker.removeEventListener('message', handler);
        resolve();
      } else if (data.type === 'error') {
        worker.removeEventListener('message', handler);
        reject(new Error(data.error));
      }
    };

    worker.addEventListener('message', handler);
    worker.postMessage({ type: 'load', modelId: mid, dtype: modelInfo.dtype });
  });
}

export function sendWebGPUMessage(messages, onToken) {
  return new Promise((resolve, reject) => {
    const worker = getWorker();
    let full = '';

    const handler = ({ data }) => {
      if (data.type === 'token') {
        full += data.token;
        onToken(data.token);
      } else if (data.type === 'done') {
        worker.removeEventListener('message', handler);
        resolve(full);
      } else if (data.type === 'error') {
        worker.removeEventListener('message', handler);
        reject(new Error(data.error));
      }
    };

    worker.addEventListener('message', handler);
    worker.postMessage({ type: 'generate', messages });
  });
}
